import { useState } from 'react';
import { Button, Input, PasswordInput } from '@mlm/ui';
import { verifyManagerPin } from '../../lib/api';

/**
 * A manager's email and PIN, verified against the server. On success it hands
 * back the short-lived approval chip the checkout sends. Used by the typed
 * cashier discount (above the branch cap), by a preset marked "needs a manager
 * PIN", and (with its own `verify`) by a large till top-up.
 */
export function ManagerPinForm({
  onApproved,
  onCancel,
  requestApproval = verifyManagerPin,
  deniedMessage = 'That user is not authorized to approve discounts.',
}: {
  onApproved: (approvalChip: string) => void;
  onCancel: () => void;
  /** Which approval to ask for; the discount override by default. */
  requestApproval?: (args: {
    managerEmail: string;
    pin: string;
  }) => Promise<{ approvalChip: string }>;
  /** What to say when the manager lacks the authority this approval needs. */
  deniedMessage?: string;
}) {
  const [managerEmail, setManagerEmail] = useState('');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  async function verify() {
    setPinError(null);
    setVerifying(true);
    try {
      const { approvalChip } = await requestApproval({ managerEmail, pin });
      setPin('');
      onApproved(approvalChip);
    } catch (err) {
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setPinError(
        code === 'invalid_pin' || code === 'manager_not_found' || code === 'manager_missing_pin'
          ? 'PIN or manager email is incorrect.'
          : code === 'manager_missing_perm'
            ? deniedMessage
            : 'Could not verify PIN.',
      );
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="space-y-2 border-t pt-2">
      <div className="space-y-1">
        <label className="text-sm text-muted-foreground">Manager email</label>
        <Input
          className="h-12 text-base"
          value={managerEmail}
          onChange={(e) => setManagerEmail(e.target.value)}
          placeholder="manager@example.com"
          autoFocus
        />
      </div>
      <div className="space-y-1">
        <label className="text-sm text-muted-foreground">PIN</label>
        <PasswordInput
          className="h-12 text-base"
          inputMode="numeric"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          placeholder="4-8 digits"
        />
      </div>
      {pinError && <p className="text-sm text-destructive">{pinError}</p>}
      <div className="flex gap-2">
        <Button className="h-12" variant="outline" onClick={onCancel} disabled={verifying}>
          Cancel
        </Button>
        <Button
          className="h-12"
          onClick={() => void verify()}
          disabled={!managerEmail || !pin || verifying}
        >
          {verifying ? 'Verifying…' : 'Verify PIN'}
        </Button>
      </div>
    </div>
  );
}
